// Move-a-job wizard.
//
// Changing a job's dates used to leave the crew behind: push a September start
// to October and everyone was still staffed for September. This walks the change
// through three questions — when does the job run now, what happens to the crew,
// and does each person's own window look right — then writes the job and the
// team members together.
//
// Opened two ways:
//   from the Planner  → starts on "dates" and collects the new dates itself
//   from Admin → Projects → starts on "crew"; the form already changed the dates

import React, { useState, useEffect } from "react";
import Icon from "./Icon";
import { planCrewMove, describeShift, shortMDY, addDays, addMonths, dayDiff, ymd } from "../services/jobMove";

const PUSHES = [
  { label: "1 week", days: 7 },
  { label: "2 weeks", days: 14 },
  { label: "1 month", months: 1 },
  { label: "2 months", months: 2 },
  { label: "3 months", months: 3 },
];

export default function MoveJobWizard({ project, members = [], initialStart, initialEnd, startStep = "dates", onCancel, onApply }) {
  const oldStart = ymd(project.StartDate);
  const oldEnd = ymd(project.CompletionDate);

  const [step, setStep] = useState(startStep);
  const [start, setStart] = useState(ymd(initialStart) || oldStart);
  const [end, setEnd] = useState(ymd(initialEnd) || oldEnd);
  // Seeded up front so opening straight on the crew step (from Admin → Projects,
  // where the dates have already been typed) has a proposal ready.
  const [rows, setRows] = useState(() =>
    planCrewMove({
      oldStart, oldEnd,
      newStart: ymd(initialStart) || oldStart,
      newEnd: ymd(initialEnd) || oldEnd,
      members,
    }).rows.map(r => ({ ...r, include: false })));
  const [mode, setMode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const plan = planCrewMove({ oldStart, oldEnd, newStart: start, newEnd: end, members });
  const movable = plan.rows.filter(r => r.changed);
  const riding = plan.rows.filter(r => r.follows);
  const staying = plan.rows.filter(r => !r.changed && !r.follows);
  const datesMoved = plan.startShift !== 0 || plan.endShift !== 0;
  const badRange = start && end && start > end;

  // Push both ends by the SAME number of days so the job keeps its length. A
  // month push is measured off the start date, so the start lands on the same
  // day of the month and the completion moves by that identical amount.
  const pushBoth = ({ days, months }) => {
    const anchor = start || end;
    const n = months ? dayDiff(anchor, addMonths(anchor, months)) : days;
    if (start) setStart(addDays(start, n));
    if (end) setEnd(addDays(end, n));
  };

  const goToCrew = () => {
    const seeded = planCrewMove({ oldStart, oldEnd, newStart: start, newEnd: end, members });
    setRows(seeded.rows.map(r => ({ ...r, include: false })));
    setMode(null);
    setStep("crew");
  };

  const choose = (which) => {
    setMode(which);
    setRows(rs => rs.map(r => ({ ...r, include: which === "all" ? r.changed : false })));
    if (which === "none") { apply([]); return; }
    setStep("review");
  };

  const setRow = (id, patch) =>
    setRows(rs => rs.map(r => (r.id === id ? { ...r, ...patch } : r)));

  const resetRow = (id) =>
    setRows(rs => rs.map(r => {
      if (r.id !== id) return r;
      const fresh = plan.rows.find(x => x.id === id);
      return fresh ? { ...r, nextStart: fresh.nextStart, nextEnd: fresh.nextEnd } : r;
    }));

  const included = rows.filter(r => r.include);

  async function apply(list) {
    setSaving(true); setErr("");
    try {
      await onApply({
        StartDate: start || null,
        EndDate: end || null,
        memberUpdates: list.map(r => ({
          Id: r.id,
          Name: r.name,
          StartDate: r.nextStart || null,
          EndDate: r.nextEnd || null,
        })),
      });
    } catch (e) {
      setErr(e.message || "Couldn't save the move.");
      setSaving(false);
    }
  }

  // Esc backs out — nothing is written until the last button.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const jobLabel = `${project.JobName || project.Title}${project.Title ? ` · ${project.Title}` : ""}`;

  return (
    <div className="mjw-backdrop" role="dialog" aria-modal="true" aria-label="Move job">
      <div className="mjw">
        <div className="mjw-head">
          <div>
            <div className="mjw-eyebrow">Move job</div>
            <div className="mjw-title">{jobLabel}</div>
          </div>
          <button className="mjw-x" onClick={onCancel} aria-label="Cancel" disabled={saving}><Icon name="x" size={18} /></button>
        </div>

        <div className="mjw-steps">
          {["dates", "crew", "review"].map((s, i) => (
            <div key={s} className={`mjw-step ${step === s ? "on" : ""} ${["dates", "crew", "review"].indexOf(step) > i ? "done" : ""}`}>
              <span className="mjw-step-n">{i + 1}</span>
              {s === "dates" ? "Dates" : s === "crew" ? "The crew" : "Review"}
            </div>
          ))}
        </div>

        <div className="mjw-body">
          {step === "dates" && (
            <>
              <div className="mjw-q">When does this job run now?</div>
              <div className="mjw-pushrow">
                <span className="mjw-pushlabel">Push the whole job</span>
                {PUSHES.map(p => (
                  <button key={p.label} className="mjw-push" onClick={() => pushBoth(p)}>+{p.label}</button>
                ))}
                <button className="mjw-push back" onClick={() => pushBoth({ months: -1 })}>−1 month</button>
              </div>

              <div className="mjw-dates">
                <div>
                  <label className="form-label">Start</label>
                  <input type="date" className="form-input" value={start} onChange={e => setStart(e.target.value)} />
                  <div className="mjw-was">was {oldStart ? shortMDY(oldStart) : "—"} · {describeShift(plan.startShift)}</div>
                </div>
                <div className="mjw-arrow"><Icon name="arrow-right" size={16} /></div>
                <div>
                  <label className="form-label">Completion</label>
                  <input type="date" className="form-input" value={end} onChange={e => setEnd(e.target.value)} />
                  <div className="mjw-was">was {oldEnd ? shortMDY(oldEnd) : "—"} · {describeShift(plan.endShift)}</div>
                </div>
              </div>

              {badRange && <div className="mjw-warn"><Icon name="alert" size={14} /> Completion is before the start date.</div>}
            </>
          )}

          {step === "crew" && (
            <>
              <div className="mjw-summary">
                {plan.slide
                  ? <>The whole job moved <b>{describeShift(plan.startShift)}</b>.</>
                  : <>
                      {plan.startShift !== 0 && <>Start moves <b>{describeShift(plan.startShift)}</b>. </>}
                      {plan.endShift !== 0 && <>Completion moves <b>{describeShift(plan.endShift)}</b>. </>}
                      {!datesMoved && <>The dates didn't change.</>}
                    </>}
              </div>

              <div className="mjw-q">
                {plan.slide ? "Do you want everybody to go with it?"
                  : plan.endShift > 0 && plan.startShift === 0 ? "Do you want to extend the people too?"
                  : "What should happen to the people on this job?"}
              </div>

              <div className="mjw-choices">
                <button className="mjw-choice" onClick={() => choose("all")} disabled={!movable.length}>
                  <div className="mjw-choice-top"><Icon name="users" size={16} /> Move everyone</div>
                  <div className="mjw-choice-sub">
                    {!movable.length ? "Nobody on this job has dates that need changing."
                      : plan.slide ? `${movable.length} ${movable.length === 1 ? "person keeps" : "people keep"} the same stint, shifted with the job.`
                      : `${movable.length} ${movable.length === 1 ? "person follows" : "people follow"} the job's new dates.`}
                  </div>
                </button>
                <button className="mjw-choice" onClick={() => choose("some")} disabled={!plan.rows.length}>
                  <div className="mjw-choice-top"><Icon name="sliders" size={16} /> Pick who moves</div>
                  <div className="mjw-choice-sub">Choose person by person, and give anyone their own dates.</div>
                </button>
                <button className="mjw-choice" onClick={() => choose("none")}>
                  <div className="mjw-choice-top"><Icon name="lock" size={16} /> Leave the crew alone</div>
                  <div className="mjw-choice-sub">Change the job's dates only. Everyone keeps the dates they have.</div>
                </button>
              </div>

              {!!riding.length && (
                <div className="mjw-note">
                  <Icon name="circle-check" size={14} />
                  {riding.length} {riding.length === 1 ? "person has" : "people have"} no dates of their own
                  ({riding.map(r => r.name).join(", ")}) — they follow the job automatically either way.
                </div>
              )}
              {!!staying.length && !!movable.length && (
                <div className="mjw-note">
                  <Icon name="alert" size={14} />
                  {staying.length} {staying.length === 1 ? "person rolls" : "people roll"} off on their own schedule
                  ({staying.map(r => r.name).join(", ")}) — nothing suggested for them. Use <b>Pick who moves</b> to change that.
                </div>
              )}
            </>
          )}

          {step === "review" && (
            <>
              <div className="mjw-q">
                {mode === "all" ? "Here's the move. Anyone with a different date?" : "Tick the people who move, and set their dates."}
              </div>
              <table className="mjw-table">
                <thead>
                  <tr>
                    <th className="mjw-c" />
                    <th>Person</th>
                    <th>Now</th>
                    <th>New start</th>
                    <th>New end</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id} className={r.include ? "on" : ""}>
                      <td className="mjw-c">
                        <input type="checkbox" checked={!!r.include} disabled={r.follows}
                          onChange={e => setRow(r.id, { include: e.target.checked })} />
                      </td>
                      <td>
                        <div className="mjw-name">{r.name}</div>
                        <div className="mjw-role">{r.role}{r.follows ? " · follows the job" : ""}</div>
                      </td>
                      <td className="mjw-now">
                        {r.follows ? "job dates" : `${r.curStart ? shortMDY(r.curStart) : "—"} → ${r.curEnd ? shortMDY(r.curEnd) : "—"}`}
                      </td>
                      <td>
                        <input type="date" className="form-input mjw-d" value={r.nextStart || ""} disabled={r.follows || !r.include}
                          onChange={e => setRow(r.id, { nextStart: e.target.value })} />
                      </td>
                      <td>
                        <input type="date" className="form-input mjw-d" value={r.nextEnd || ""} disabled={r.follows || !r.include}
                          onChange={e => setRow(r.id, { nextEnd: e.target.value })} />
                      </td>
                      <td>
                        {r.include && r.changed && (
                          <button className="mjw-reset" onClick={() => resetRow(r.id)} title="Back to the suggested dates">reset</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.some(r => r.include && r.nextStart && r.nextEnd && r.nextStart > r.nextEnd) && (
                <div className="mjw-warn"><Icon name="alert" size={14} /> Someone finishes before they start — check the dates above.</div>
              )}
            </>
          )}

          {err && <div className="mjw-warn"><Icon name="alert" size={14} /> {err}</div>}
        </div>

        <div className="mjw-foot">
          <button className="btn btn-ghost" onClick={step === "dates" ? onCancel : () => setStep(step === "review" ? "crew" : "dates")} disabled={saving}>
            {step === "dates" ? "Cancel" : "Back"}
          </button>
          <div className="mjw-foot-right">
            {step === "dates" && (
              <button className="btn btn-primary" onClick={goToCrew} disabled={badRange || !datesMoved}>
                {datesMoved ? "Next — the crew" : "Change a date to continue"}
              </button>
            )}
            {step === "review" && (
              <button className="btn btn-primary" onClick={() => apply(included)} disabled={saving}>
                {saving ? "Saving…" : included.length
                  ? `Move the job and ${included.length} ${included.length === 1 ? "person" : "people"}`
                  : "Move the job only"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
